import { AuthorComposer, ContributorRole, Cradle, AuthorComposerRights } from '@solo/app-core';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Flex, Text, Button } from '@/app/ui/atoms';
import {
  InputWithLabel,
  InputTextAutocomplete,
  NumericInputWithLabel,
  RadioButtonWithLabel,
} from '@/app/ui/molecules';
import { translate } from '@/app/i18n';
import { useCradle } from '@/app/contexts';
import { useBlocState } from '@/app/hooks';

interface RecordingAuthorComposerPopUpProps {
  saveRecordingAuthor: (recordingAuthor: AuthorComposer) => void;
  closeModal: () => void;
  currentRecordingAuthors: AuthorComposer[];
  roles: ContributorRole[];
}

const authorComposersRights = Object.values(AuthorComposerRights)
  .filter((right) => typeof right === 'number')
  .map((right) => ({
    label: translate(`AUTHOR_COMPOSER_RIGHTS.${right}`),
    value: right.toString(),
  }));

const RecordingAuthorComposerPopUp = (props: RecordingAuthorComposerPopUpProps) => {
  const { saveRecordingAuthor, closeModal, currentRecordingAuthors, roles } = props;
  const [newRecordingAuthor, setNewRecordingAuthor] = useState<Partial<AuthorComposer>>({});
  const { musicalWorksBloc, authBloc } = useCradle<Cradle>();
  const authState = useBlocState(authBloc);

  const [newAuthorComposerName, setNewAuthorComposerName] = useState<string | undefined>();
  const [existingAuthorComposer, setExistingAuthorComposer] = useState<
    AuthorComposer | undefined
  >();

  const [recordingAuthors, setRecordingAuthors] =
    useState<AuthorComposer[]>(currentRecordingAuthors);
  const [searching, setSearching] = useState<boolean>(false);
  const [authorComposers] = useState<AuthorComposer[]>([]);

  // useEffect(() => {
  //   async function loadAuthorComposers() {
  //     if (authState.kind !== 'AuthenticatedAuthState') return;
  //     const authComps = await musicalWorksBloc.getAuthorComposers();
  //     if (!authComps) return;
  //     setAuthorComposers(authComps);
  //   }
  //   loadAuthorComposers();
  // }, []);

  useEffect(() => {
    setRecordingAuthors(currentRecordingAuthors);
  }, [currentRecordingAuthors]);

  async function addRecordingAuthor() {
    if (
      (newRecordingAuthor.authorComposerName === undefined && !searching) ||
      (newRecordingAuthor === undefined && searching) ||
      newRecordingAuthor?.roleId === undefined ||
      newRecordingAuthor?.mechanicalSplit === undefined ||
      newRecordingAuthor?.rightsId === undefined
    ) {
      toast.error(translate('TOAST.MANDATORY_ERROR'));
      return;
    }

    if (
      (newRecordingAuthor.mechanicalSplit ||
        0 +
          recordingAuthors
            .filter((author) => author.mechanicalSplit)
            .reduce((perf, cur) => cur.mechanicalSplit || 0 + perf, 0)) > 100
    ) {
      toast.error(translate('TOAST.MECH_100_ERROR'));
      return;
    }
    let authorComposer: AuthorComposer | undefined;

    if (!searching) {
      if (authState.kind !== 'AuthenticatedAuthState' || !newRecordingAuthor.authorComposerName)
        return;
      authorComposer = {
        ...newRecordingAuthor,
        roleId: newRecordingAuthor.roleId || 0,
        mechanicalSplit: newRecordingAuthor.mechanicalSplit || 0,
        rightsId: newRecordingAuthor.rightsId,
        publisherName: newRecordingAuthor.publisherName || '',
        publisherId: undefined,
        userId: authState.user.id,
        ipi: newRecordingAuthor.ipi || '',
      } as AuthorComposer;
    } else {
      if (
        authState.kind !== 'AuthenticatedAuthState' ||
        !existingAuthorComposer ||
        !existingAuthorComposer.authorComposerId
      )
        return;
      try {
        authorComposer = await musicalWorksBloc.searchAuthorComposerByComposerId(
          existingAuthorComposer.authorComposerId,
        );
      } catch (error) {
        toast.error(translate('TOAST.CREATE_AUTHOR_ERROR'));
      }
    }

    if (!authorComposer) return;

    saveRecordingAuthor({
      ...authorComposer,
      mechanicalSplit: newRecordingAuthor.mechanicalSplit,
      roleId: newRecordingAuthor.roleId,
    });
    closeModal();
  }

  function addExistingRecordingAuthor(recordingAuthorId: string) {
    const authorComposer = authorComposers.find((ac) => ac.authorComposerId === recordingAuthorId);
    if (!authorComposer) return;
    setExistingAuthorComposer(authorComposer);
  }

  return (
    <Flex.Container gap="30px" flexDirection="column" padding="0px 130px 0px 130px">
      <Flex.Container gap="10px" alignItems="center">
        <Text.SubtitleBold>{`${translate(
          'WORKS_PAGES.ADD_NEW_AUTHOR_COMPOSER',
        )}`}</Text.SubtitleBold>
      </Flex.Container>
      <Flex.Container gap="10px" alignItems="center">
        <Text.SubtitleBold>{`${translate('WORKS_PAGES.AUTHOR_COMPOSER_DATA')}`}</Text.SubtitleBold>
      </Flex.Container>
      {authorComposers.length > 0 && (
        <>
          <RadioButtonWithLabel
            checked={searching}
            onChange={() => setSearching(true)}
            label={`${translate('WORKS_PAGES.SEARCH_AUTHOR_COMPOSER')}`}
          />
          <InputTextAutocomplete
            placeholder={`${translate('WORKS_PAGES.SEARCH_NAME_COMPOSERID')}`}
            label={`${translate('WORKS.SEARCH')}`}
            items={authorComposers.map((ac) => ({
              label: ac.authorComposerName || '',
              value: ac.authorComposerId || '',
            }))}
            onChange={(recordingAuthorId: string) => addExistingRecordingAuthor(recordingAuthorId)}
            disabled={!searching}
          />
        </>
      )}
      <InputWithLabel
        label={translate('WORKS_PAGES.FULLNAME')}
        placeholder={translate('WORKS_PAGES.FULLNAME')}
        onChange={(name) => {
          setNewAuthorComposerName(name);
          setNewRecordingAuthor({
            ...newRecordingAuthor,
            authorComposerName: name,
          });
        }}
        value={newAuthorComposerName}
        disabled={searching}
      />
      <Flex.Container gap="10px" alignItems="center">
        <Text.SubtitleBold>{`${translate('WORKS_PAGES.AUTHOR_COMPOSER_RULES')}`}</Text.SubtitleBold>
      </Flex.Container>
      <InputWithLabel
        label={translate('WORKS_PAGES.IPI')}
        placeholder={translate('WORKS_PAGES.IPI')}
        onChange={(ipi) => {
          setNewRecordingAuthor({
            ...newRecordingAuthor,
            ipi,
          });
        }}
        value={newRecordingAuthor.ipi}
      />
      <Flex.Container gap="16px">
        <Flex.Container flex={5}>
          <InputTextAutocomplete
            label={translate('TRACKS.ROLE')}
            placeholder={translate('WORKS_PAGES.TYPES_OF_ROLE')}
            items={Array.from(roles, (role: ContributorRole) => ({
              value: role.id.toString(),
              label: role.name,
            }))}
            onChange={(role) =>
              setNewRecordingAuthor({
                ...newRecordingAuthor,
                roleId: Number(role),
              })
            }
            defaultOption={
              newRecordingAuthor.roleId
                ? {
                    value: newRecordingAuthor.roleId.toString(),
                    label: roles.find((r) => r.id === newRecordingAuthor.roleId)?.name || '',
                  }
                : undefined
            }
          />
        </Flex.Container>

        <Flex.Container flex={1}>
          <NumericInputWithLabel
            value={newRecordingAuthor.mechanicalSplit}
            label={translate('TRACKS.SHARES')}
            placeholder="100%"
            onChange={(mechanicalSplit) =>
              setNewRecordingAuthor({
                ...newRecordingAuthor,
                mechanicalSplit,
              })
            }
          />
        </Flex.Container>
      </Flex.Container>
      <Flex.Container gap="16px">
        <InputTextAutocomplete
          placeholder={`${translate('WORKS_PAGES.PUBLISHING_TYPE')}`}
          label={`${translate('WORKS_PAGES.PUBLISHING_TYPE')}`}
          items={authorComposersRights}
          onChange={(rightsId: string) => {
            setNewRecordingAuthor({
              ...newRecordingAuthor,
              rightsId: Number(rightsId),
            });
          }}
        />
        {newRecordingAuthor.rightsId &&
          newRecordingAuthor.rightsId === AuthorComposerRights.PUBLISHED && (
            <InputWithLabel
              label={translate('WORKS_PAGES.PUBLISHER_NAME')}
              placeholder={translate('WORKS_PAGES.PUBLISHER_NAME')}
              onChange={(publisherName) => {
                setNewRecordingAuthor({
                  ...newRecordingAuthor,
                  publisherName,
                });
              }}
              value={newRecordingAuthor.publisherName}
            />
          )}
      </Flex.Container>
      <Flex.Container justifyContent="flex-end" gap="5px">
        <Button $size="md" $transparent $variant="primary" onClick={() => closeModal()}>
          <Text.SectionSubtitle style={{ padding: 0 }} fontWeight={600}>
            {translate('PROFILE_INFO.CANCEL')}
          </Text.SectionSubtitle>
        </Button>
        <Button
          $size="md"
          $outline
          $transparent
          $variant="primary"
          onClick={() => addRecordingAuthor()}
        >
          <Text.SectionSubtitle style={{ padding: 0 }} fontWeight={600}>
            {translate('BUTTON.SAVE_ADD')}
          </Text.SectionSubtitle>
        </Button>
      </Flex.Container>
    </Flex.Container>
  );
};

export default RecordingAuthorComposerPopUp;
